import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import LazyImage from "./LazyImage";
import VerificationBadge from "./VerificationBadge";
import ModalPortal from "./ModalPortal";

type Like = {
    id: number;
    postId: number;
    userId: number;
    createdAt: string;
    user: {
      id: number;
      username: string;
      name: string;
      image: string;
      verified?: boolean;
    };
  };

type LikesModalProps = {
  isOpen: boolean;
  onClose: () => void;
  likes: Like[];
};


const LikesModal: React.FC<LikesModalProps> = ({ isOpen, onClose, likes }) => {
    const navigate = useNavigate();

    const handleUserClick = (like: Like) => {
      onClose();
      navigate(`/${like.user.username || like.user.id}`);
    };

    return (
      <ModalPortal>
      <AnimatePresence>
        {isOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 z-[100] flex items-center justify-center p-4"
            onClick={onClose}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="
                bg-white dark:bg-gray-900
                rounded-xl
                w-full max-w-sm
                max-h-[40vh]
                flex flex-col
              "
            >
              {/* Header (fixed) */}
              <div className="border-b border-gray-200 dark:border-gray-700 p-4">
                <h3 className="text-center font-semibold text-gray-900 dark:text-white">
                  لایک‌ها
                </h3>
              </div>
  
              {/* Scrollable content */}
              <div className="flex-1 overflow-y-auto px-4 py-3 space-y-3">
                {likes.length === 0 ? (
                  <p className="text-center text-gray-500 text-sm">
                    هنوز لایکی وجود ندارد
                  </p>
                ) : (
                  likes.map((like) => (
                    <button
                      key={like.id}
                      type="button"
                      onClick={() => handleUserClick(like)}
                      className="flex items-end w-full text-end hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg p-2 transition-colors"
                      aria-label={`View ${like.user.name || like.user.username} profile`}
                    >
                      <div className="flex items-center">
                        <LazyImage
                          src={like.user.image || "/default-avatar.svg"}
                          alt={like.user.name || like.user.username}
                          className="w-9 h-9 rounded-full object-cover me-2"
                        />
                        <div className="flex flex-col">
                          <span className="text-sm font-medium text-start text-gray-900 dark:text-gray-100">
                            {like.user.name || like.user.username}
                          </span>
                          <span className="text-xs text-gray-500">
                            @{like.user.username}
                          </span>
                        </div>
                      </div>
  
                      {like.user.verified && (
                        <VerificationBadge className="mt-1 me-1" verified size="sm" />
                      )}
                    </button>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
      </ModalPortal>
    );
  };
  

export default LikesModal;

